import { currencyFormat, isBadAmount, checkDateFormat } from '../helpers.js';
import { parseAmount } from './upgradeListUtils.js';
import {
  getEffectiveQualificationScore,
  getGrantRecordId,
} from './qualificationCheck.js';

export const GRANT_SORT_AMOUNT = 'amount';
export const GRANT_SORT_DEADLINE = 'deadline';
export const GRANT_SORT_FIT = 'fit';
export const GRANT_SORT_RELEVANCE = 'relevance';
export const FREEBIE_GRANTS_COUNT = 3;

export const GRANT_SORT_OPTIONS = [
  { value: GRANT_SORT_AMOUNT, label: 'Amount: High → Low' },
  { value: GRANT_SORT_DEADLINE, label: 'Deadline: Most Urgent' },
  { value: GRANT_SORT_FIT, label: 'Fit Score: High → Low' },
];

/** All Grants finder — relevance default, then deadline / amount */
export const ALL_GRANTS_SORT_OPTIONS = [
  { value: GRANT_SORT_RELEVANCE, label: 'Most Relevant' },
  { value: GRANT_SORT_DEADLINE, label: 'Deadline: Most Urgent' },
  { value: GRANT_SORT_AMOUNT, label: 'Amount: High → Low' },
];

const MS_PER_DAY = 86400000;

function getGrantAmount(grant) {
  const fields = grant?.fields || {};
  return parseAmount(fields.AnnualMargin ?? fields.Amount ?? fields.ExpectedValue);
}

function compareGrantsByAmountDesc(grantA, grantB, variant = 'nonFreebie') {
  const aBad = isBadAmount(grantA?.fields?.AnnualMargin);
  const bBad = isBadAmount(grantB?.fields?.AnnualMargin);
  if (aBad && bBad) return 0;
  if (aBad) return variant === 'freebie' ? -1 : 1;
  if (bBad) return variant === 'freebie' ? 1 : -1;
  return getGrantAmount(grantB) - getGrantAmount(grantA);
}

function getDeadlineUrgencyRank(deadline) {
  if (deadline == null || deadline === '') return 1_000_000;

  const normalized = String(deadline).trim().toLowerCase();
  if (normalized === 'closed') return 900_000;
  // Rolling / continuous / open sit after dated future deadlines
  if (
    normalized === 'continuous' ||
    normalized === 'rolling' ||
    normalized === 'ongoing' ||
    normalized === 'open'
  ) {
    return 800_000;
  }
  if (!checkDateFormat(String(deadline))) return 1_000_000;

  const parts = String(deadline).split('-').map((part) => parseInt(part, 10));
  const deadlineDate = new Date(parts[0], parts[1] - 1, parts[2]);
  if (Number.isNaN(deadlineDate.getTime())) return 1_000_000;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  deadlineDate.setHours(0, 0, 0, 0);
  const daysUntilDeadline = Math.ceil((deadlineDate.getTime() - today.getTime()) / MS_PER_DAY);

  // Future (and today) first — soonest first; past deadlines after continuous/open
  if (daysUntilDeadline < 0) return 850_000 + Math.abs(daysUntilDeadline);
  return daysUntilDeadline;
}

function compareGrantsByDeadlineUrgency(grantA, grantB) {
  return (
    getDeadlineUrgencyRank(grantA?.fields?.Deadline) -
    getDeadlineUrgencyRank(grantB?.fields?.Deadline)
  );
}

function getGrantFitScore(grant, qualificationCache = {}) {
  const grantId = getGrantRecordId(grant);
  if (!grantId) return null;
  const data = qualificationCache?.[grantId];
  if (!data) return null;
  return getEffectiveQualificationScore(data.qualificationScore, data.qualificationStatus);
}

function compareGrantsByFitScoreDesc(grantA, grantB, qualificationCache = {}) {
  const scoreA = getGrantFitScore(grantA, qualificationCache);
  const scoreB = getGrantFitScore(grantB, qualificationCache);
  if (scoreA == null && scoreB == null) return 0;
  if (scoreA == null) return 1;
  if (scoreB == null) return -1;
  return scoreB - scoreA;
}

/** Search relevance: lower _relevanceScore is better; then more keyword matches. Falls back to amount. */
function compareGrantsByRelevance(grantA, grantB, variant = 'nonFreebie') {
  const scoreA = grantA?._relevanceScore;
  const scoreB = grantB?._relevanceScore;
  const hasScoreA = scoreA != null && Number.isFinite(Number(scoreA));
  const hasScoreB = scoreB != null && Number.isFinite(Number(scoreB));
  if (hasScoreA && hasScoreB && scoreA !== scoreB) return scoreA - scoreB;
  if (hasScoreA && !hasScoreB) return -1;
  if (!hasScoreA && hasScoreB) return 1;

  const matchesA = grantA?._totalUniqueMatches || 0;
  const matchesB = grantB?._totalUniqueMatches || 0;
  if (matchesA !== matchesB) return matchesB - matchesA;

  const titleA = grantA?._titleMatches || 0;
  const titleB = grantB?._titleMatches || 0;
  if (titleA !== titleB) return titleB - titleA;

  return compareGrantsByAmountDesc(grantA, grantB, variant);
}

function applyGrantSort(
  grants,
  sortBy = GRANT_SORT_AMOUNT,
  variant = 'nonFreebie',
  qualificationCache = {}
) {
  const sorted = [...grants];
  if (sortBy === GRANT_SORT_DEADLINE) {
    sorted.sort(compareGrantsByDeadlineUrgency);
  } else if (sortBy === GRANT_SORT_FIT) {
    sorted.sort((a, b) => compareGrantsByFitScoreDesc(a, b, qualificationCache));
  } else if (sortBy === GRANT_SORT_RELEVANCE) {
    sorted.sort((a, b) => compareGrantsByRelevance(a, b, variant));
  } else {
    sorted.sort((a, b) => compareGrantsByAmountDesc(a, b, variant));
  }
  sorted.sort(compareGrantsByOpenOrClosed);
  return sorted;
}

/**
 * Compare two grant records by OpenOrClosed (closed grants sort after open).
 * Mirrors GrantsSectionPage behavior.
 */
export function compareGrantsByOpenOrClosed(grantRecordA, grantRecordB) {
  const aClosed = grantRecordA?.fields?.OpenOrClosed?.trim()?.toLowerCase() === 'closed';
  const bClosed = grantRecordB?.fields?.OpenOrClosed?.trim()?.toLowerCase() === 'closed';

  if (aClosed && bClosed) return 0;
  if (aClosed) return 1;
  if (bClosed) return -1;
  return 0;
}

function getInterestedAndFlaggedNames(interestedInGrants, flaggedGrants) {
  const interestedNames = (interestedInGrants || []).map((g) => g.fields?.Name).filter(Boolean);
  const flaggedNames = (flaggedGrants || []).map((g) => g.fields?.Name).filter(Boolean);
  return { interestedNames, flaggedNames };
}

/** Full recommended-for-you list (sorted); first FREEBIE_GRANTS_COUNT are visitor freebies. */
export function getRecommendedGrantsPool(
  grantData,
  interestedInGrants,
  flaggedGrants = [],
  sortBy = GRANT_SORT_AMOUNT,
  qualificationCache = {}
) {
  const { interestedNames, flaggedNames } = getInterestedAndFlaggedNames(interestedInGrants, flaggedGrants);
  const filtered = (grantData || [])
    .filter((upgrade) => !interestedNames.includes(upgrade.fields?.Name))
    .filter((upgrade) => !flaggedNames.includes(upgrade.fields?.Name));

  return applyGrantSort(filtered, sortBy, 'nonFreebie', qualificationCache);
}

export function getFreebieGrants(
  grantData,
  interestedInGrants,
  flaggedGrants = [],
  sortBy = GRANT_SORT_AMOUNT,
  qualificationCache = {}
) {
  return getRecommendedGrantsPool(
    grantData,
    interestedInGrants,
    flaggedGrants,
    sortBy,
    qualificationCache
  ).slice(0, FREEBIE_GRANTS_COUNT);
}

export function getNonFreebieGrants(
  grantData,
  interestedInGrants,
  flaggedGrants,
  sortBy = GRANT_SORT_AMOUNT,
  qualificationCache = {}
) {
  return getRecommendedGrantsPool(
    grantData,
    interestedInGrants,
    flaggedGrants,
    sortBy,
    qualificationCache
  ).slice(FREEBIE_GRANTS_COUNT);
}

export function getInterestedInGrantsSorted(
  interestedGrantsArray,
  sortBy = GRANT_SORT_AMOUNT,
  qualificationCache = {}
) {
  return applyGrantSort(interestedGrantsArray, sortBy, 'nonFreebie', qualificationCache);
}

export function getAiRecommendedGrantsSorted(
  aiRecommendedGrantsArray,
  sortBy = GRANT_SORT_AMOUNT,
  qualificationCache = {}
) {
  return applyGrantSort(aiRecommendedGrantsArray, sortBy, 'nonFreebie', qualificationCache);
}

export function sortGrantsByMode(
  grants,
  sortBy = GRANT_SORT_AMOUNT,
  qualificationCache = {}
) {
  return applyGrantSort(grants, sortBy, 'nonFreebie', qualificationCache);
}

/** Human-readable deadline / days-left for mobile grant cards */
export function grantDeadlineDaysLabel(deadline) {
  if (deadline == null) return '—';
  const s = String(deadline).trim();
  const lower = s.toLowerCase();
  if (lower === 'continuous') return 'Rolling';
  if (lower === 'closed') return 'Closed';

  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return '—';
  const parts = s.split('-').map((p) => parseInt(p, 10));
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  if (Number.isNaN(d.getTime())) return '—';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  const diff = Math.ceil((d.getTime() - today.getTime()) / MS_PER_DAY);
  if (diff < 0) return 'Past deadline';
  if (diff === 0) return 'Today';
  if (diff === 1) return '1 day left';
  return `${diff} days left`;
}

export function getGrantBenefitStatement(grant) {
  const f = grant?.fields || {};
  return (
    f?.ExcitingBenefit?.value ??
    f?.ExcitingBenefitAlt?.value ??
    (typeof f?.ExcitingBenefit === 'string' ? f.ExcitingBenefit : null) ??
    (typeof f?.ExcitingBenefitAlt === 'string' ? f.ExcitingBenefitAlt : null) ??
    '50% government subsidy R&D, hiring, market expansion'
  );
}

export function formatGrantCardAmount(grant) {
  const f = grant?.fields || {};
  let n = f.AnnualMargin;
  if (n == null || n === '') n = f.ExpectedValue;
  if (n == null || n === '') n = parseAmount(f.Amount);
  if (typeof n === 'string') n = parseFloat(n);
  if (n == null || Number.isNaN(Number(n))) return 'N/A';
  return currencyFormat(Number(n));
}
